import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Settings, Mail } from 'lucide-react';
import { VISIBLE_STATIONS, type VisibleStation } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import { SkeletonList } from '../../components/ui/Skeleton';
import { api, ApiError } from '../../lib/api';

interface Row {
  station: 'IT' | 'PPE' | 'Tools' | 'Fleet' | 'DBSHR';
  email_receipt_on_issue: boolean;
  updated_at: string;
  updated_by_user_id: string | null;
}

const ALLOWED = ['admin', 'super_admin'];

const STATION_NOTES: Record<Row['station'], string> = {
  IT: 'Laptops, phones, asset-tracked equipment. Default ON — high-value items warrant a paper trail.',
  Fleet: 'Vehicles, van keys. Default ON — keys handover is the most important thing to document.',
  PPE: 'Boots, hi-vis, hard hats. Default OFF — receipts every kit-up would feel spammy.',
  Tools: 'Hand tools, consumables. Default OFF.',
  DBSHR: 'DBS / HR documents. Default OFF — paperwork lives in HR systems.',
};

/**
 * Per-station knobs. v1 just controls whether a kit-issuance receipt
 * PDF is emailed to the attendee (the PDF is always rendered and
 * snapshotted; this toggle only controls the email).
 */
export function StationSettings() {
  const { data: user, isLoading } = useCurrentUser();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !user.roles.some((r) => ALLOWED.includes(r))) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ rows: Row[] }>('/api/admin/station-settings');
        if (!cancelled) setRows(r.rows);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'load failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Station settings is restricted to admin / super_admin.
      </main>
    );
  }

  async function toggle(station: Row['station'], next: boolean) {
    setSaving(station);
    try {
      const r = await api<{ setting: Row }>(
        `/api/admin/station-settings/${station}`,
        {
          method: 'PUT',
          body: JSON.stringify({ email_receipt_on_issue: next }),
        },
      );
      setRows((prev) =>
        prev?.map((x) => (x.station === station ? r.setting : x)) ?? prev,
      );
      toast.success(
        `${station}: receipts ${next ? 'will be emailed' : 'will not be emailed'}`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'save failed');
    } finally {
      setSaving(null);
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-surface-2 text-fg-muted">
            <Settings className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-fg-muted">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Station settings
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Per-station configuration. Today: whether to email the attendee
              their signed kit-receipt PDF after each issuance. The PDF is{' '}
              <strong>always</strong> rendered and stored regardless; this
              toggle only controls the email.
            </p>
          </div>
        </header>

        {error ? (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="mt-6 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {rows === null ? (
            <div className="p-5">
              <SkeletonList rows={5} />
            </div>
          ) : (
            <ul className="divide-y divide-divider/10">
              {rows
                .filter((r): r is Row & { station: VisibleStation } =>
                  (VISIBLE_STATIONS as readonly string[]).includes(r.station),
                )
                .map((r) => (
                <li key={r.station} className="px-5 py-4 sm:px-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold tracking-tight">
                          {r.station}
                        </span>
                        {r.email_receipt_on_issue ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-success-soft/15 px-2 py-0.5 text-[11px] font-medium text-success-soft-fg ring-1 ring-success-soft/25">
                            <Mail className="h-3 w-3" />
                            Email on
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-fg-muted ring-1 ring-line/15">
                            Email off
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-fg-muted">
                        {STATION_NOTES[r.station]}
                      </p>
                    </div>
                    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={r.email_receipt_on_issue}
                        disabled={saving === r.station}
                        onChange={(e) => toggle(r.station, e.target.checked)}
                        className="peer sr-only"
                      />
                      <span className="h-6 w-11 rounded-full bg-surface-inset transition-colors peer-checked:bg-emerald-500" />
                      <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-surface shadow transition-transform peer-checked:translate-x-5" />
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}
